import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchApi } from '@/services/api';
import type { Paginated, PlanScopeFamily } from '../types/consensus.types';

interface CodeNameRow {
  id: number;
  code?: string;
  client_code?: string;
  name: string;
}

export interface ScopeOption {
  code: string;
  label: string;
  id?: number;
}

function rowCode(row: CodeNameRow): string {
  return row.code ?? row.client_code ?? String(row.id);
}

function mapRows(rows: CodeNameRow[]): ScopeOption[] {
  return rows.map((r) => {
    const code = rowCode(r);
    return {
      code,
      label: r.name ? `${code} — ${r.name}` : code,
    };
  });
}

async function fetchAllPages<T>(endpoint: string): Promise<T[]> {
  const out: T[] = [];
  let page = 1;
  for (;;) {
    const data = await fetchApi<Paginated<T>>(endpoint, { params: { page_size: 500, page } });
    out.push(...(data.results ?? []));
    if (!data.next) break;
    page += 1;
  }
  return out;
}

function buildFamilyMapsFromPlan(planFamilies: PlanScopeFamily[]) {
  const familyOptions: ScopeOption[] = planFamilies.map((f) => {
    const code = f.client_code?.trim() || String(f.id);
    return {
      code,
      label: `${f.client_code} — ${f.name}`,
    };
  });
  const familyCodeToId = new Map<string, number>();
  const familyIdToCode = new Map<number, string>();
  for (const f of planFamilies) {
    const code = f.client_code?.trim() || String(f.id);
    familyCodeToId.set(code, f.id);
    familyIdToCode.set(f.id, code);
  }
  return { familyOptions, familyCodeToId, familyIdToCode };
}

function buildScopeCodeMaps(rows: CodeNameRow[]): {
  options: ScopeOption[];
  codeToId: Map<string, number>;
  idToCode: Map<number, string>;
} {
  const codeCounts = new Map<string, number>();
  for (const r of rows) {
    const plain = rowCode(r);
    codeCounts.set(plain, (codeCounts.get(plain) ?? 0) + 1);
  }
  const hasDupes = [...codeCounts.values()].some((count) => count > 1);
  const options: ScopeOption[] = rows.map((r) => {
    const plain = rowCode(r);
    const code = hasDupes && (codeCounts.get(plain) ?? 0) > 1 ? `id:${r.id}` : plain;
    return {
      code,
      label: r.name ? `${plain} — ${r.name}` : plain,
    };
  });
  const codeToId = new Map<string, number>();
  const idToCode = new Map<number, string>();
  for (const r of rows) {
    const plain = rowCode(r);
    const code = hasDupes && (codeCounts.get(plain) ?? 0) > 1 ? `id:${r.id}` : plain;
    codeToId.set(code, r.id);
    idToCode.set(r.id, code);
  }
  return { options, codeToId, idToCode };
}

function buildFamilyMapsFromCatalog(rows: CodeNameRow[]) {
  const { options, codeToId, idToCode } = buildScopeCodeMaps(rows);
  return { familyOptions: options, familyCodeToId: codeToId, familyIdToCode: idToCode };
}

export type ScopeFilterOptions = ReturnType<typeof useScopeFilterOptions>;

export function useScopeFilterOptions(
  enabled = true,
  planFamilies?: PlanScopeFamily[] | null,
) {
  const usePlanFamilies = (planFamilies?.length ?? 0) > 0;

  const familiesQ = useQuery({
    queryKey: ['scope-filter', 'families'],
    queryFn: () => fetchAllPages<CodeNameRow>('products/families/'),
    enabled: enabled && !usePlanFamilies,
  });
  const subfamiliesQ = useQuery({
    queryKey: ['scope-filter', 'subfamilies'],
    queryFn: () => fetchAllPages<CodeNameRow>('products/subfamilies/'),
    enabled,
  });
  const warehousesQ = useQuery({
    queryKey: ['scope-filter', 'warehouses'],
    queryFn: () => fetchAllPages<CodeNameRow>('products/warehouses/'),
    enabled,
  });
  const suppliersQ = useQuery({
    queryKey: ['scope-filter', 'suppliers'],
    queryFn: () => fetchAllPages<CodeNameRow>('suppliers/'),
    enabled,
  });

  const familyMaps = useMemo(() => {
    if (usePlanFamilies && planFamilies) {
      return buildFamilyMapsFromPlan(planFamilies);
    }
    return buildFamilyMapsFromCatalog(familiesQ.data ?? []);
  }, [usePlanFamilies, planFamilies, familiesQ.data]);

  const subfamilyMaps = useMemo(
    () => buildScopeCodeMaps(subfamiliesQ.data ?? []),
    [subfamiliesQ.data],
  );

  const warehouseOptions = useMemo(
    () => mapRows(warehousesQ.data ?? []),
    [warehousesQ.data],
  );
  const supplierOptions = useMemo(
    () => mapRows(suppliersQ.data ?? []),
    [suppliersQ.data],
  );

  const subfamilyCodeToId = subfamilyMaps.codeToId;

  const subfamilyIdToCode = subfamilyMaps.idToCode;

  const warehouseCodeToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of warehousesQ.data ?? []) m.set(rowCode(r), r.id);
    return m;
  }, [warehousesQ.data]);

  const supplierCodeToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of suppliersQ.data ?? []) m.set(rowCode(r), r.id);
    return m;
  }, [suppliersQ.data]);

  const warehouseIdToCode = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of warehousesQ.data ?? []) m.set(r.id, rowCode(r));
    return m;
  }, [warehousesQ.data]);

  const supplierIdToCode = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of suppliersQ.data ?? []) m.set(r.id, rowCode(r));
    return m;
  }, [suppliersQ.data]);

  return {
    isLoading:
      (!usePlanFamilies && familiesQ.isLoading) ||
      subfamiliesQ.isLoading ||
      warehousesQ.isLoading ||
      suppliersQ.isLoading,
    familyOptions: familyMaps.familyOptions,
    subfamilyOptions: subfamilyMaps.options,
    warehouseOptions,
    supplierOptions,
    familyCodeToId: familyMaps.familyCodeToId,
    subfamilyCodeToId,
    warehouseCodeToId,
    supplierCodeToId,
    familyIdToCode: familyMaps.familyIdToCode,
    subfamilyIdToCode,
    warehouseIdToCode,
    supplierIdToCode,
    usesPlanFamilies: usePlanFamilies,
  };
}
